import { ButtonWithLoading } from "@/controls/buttons/ButtonWithLoading";
import {
	Alert,
	Box,
	Button,
	CircularProgress,
	Grid2 as Grid,
	Paper,
	TextField,
} from "@mui/material";
import { Control, Controller } from "react-hook-form";
import { SampleAttributesForm } from "../hooks/useSampleEditor";
import { DateTimePickerDE } from "./DateTimePickerDE";
import { NotesFieldDE } from "./NotesFieldDE";
import { SampleSelector } from "./SampleSelector";

interface SelectedSample {
	ChemistrySampleId: number | null;
	SampleTypeName: string | null;
	SampleSequence: number | null;
	ResultSourceId: number | null;
}

interface SampleDataProps {
	control: Control<SampleAttributesForm>;
	selectedSample: SelectedSample | null;
	isLoading: boolean;
	isSaving: boolean;
	isReadOnly?: boolean;
	readOnlyMessage?: string;
	onSubmit: () => void;
	onSampleSelection: (value: number | null, sampleTypeName: string) => void;
	onFieldChange: (field: keyof SampleAttributesForm, value: any) => void;
	onEditingStarted: () => void;
	onEditingStopped: () => void;
	onCancel: () => void;
}

export const SampleData = ({
	control,
	selectedSample,
	isLoading,
	isSaving,
	isReadOnly = false,
	readOnlyMessage,
	onSubmit,
	onSampleSelection,
	onFieldChange,
	onEditingStarted,
	onEditingStopped,
	onCancel,
}: SampleDataProps) => {
	const isDisabled = isLoading || !selectedSample || isReadOnly;

	return (
		<Grid container>
			<Grid size={{ xs: 12 }}>
				<Paper
					sx={{
						width: "100%",
						border: "1px solid #ccc",
						borderRadius: 1,
						boxSizing: "border-box",
					}}
					elevation={0}
				>
					<Box padding={2}>
						{isReadOnly && readOnlyMessage && (
							<Alert severity="warning" sx={{ marginBottom: 2 }}>
								{readOnlyMessage}
							</Alert>
						)}
						<form onSubmit={onSubmit} style={{ width: "100%" }}>
							<Grid container spacing={2}>
								<Grid size={{ xs: 12, sm: 6, md: 2 }}>
									<Grid container spacing={1} direction="column">
										<Grid size={{ xs: 12 }}>
											<Controller
												name="SampleTypeId"
												control={control}
												rules={{ required: "Sample Code is required." }}
												render={({ field, fieldState: { error } }) => (
													<SampleSelector
														{...field}
														value={field.value}
														onChange={onSampleSelection}
														onOpen={onEditingStarted}
														onClose={onEditingStopped}
														fieldError={error}
														disabled={isDisabled}
														loading={isLoading}
													/>
												)}
											/>
										</Grid>
										<Grid size={{ xs: 12 }}>
											<Controller
												name="Reported"
												control={control}
												rules={{ required: "Date & Time is required." }}
												render={({ field, fieldState: { error } }) => (
													<DateTimePickerDE
														label="Date & Time"
														value={field.value}
														format="MM/DD/YYYY HH:mm"
														onChange={(date) => onFieldChange("Reported", date)}
														onBlur={() => {}}
														onFocus={onEditingStarted}
														fieldError={error}
														disabled={isDisabled}
													/>
												)}
											/>
										</Grid>
									</Grid>
								</Grid>

								<Grid size={{ xs: 12, sm: 6, md: 2 }}>
									<Grid container spacing={1} direction="column">
										<Grid size={{ xs: 12 }}>
											<Controller
												name="SampleSequence"
												control={control}
												rules={{
													required: "Order is required.",
													min: { value: 1, message: "Order must be positive" },
													max: { value: 10, message: "Order cannot exceed 10" },
												}}
												render={({ field, fieldState: { error } }) => (
													<TextField
														{...field}
														label="Order"
														variant="outlined"
														size="small"
														fullWidth
														type="number"
														value={field.value ?? ""}
														error={!!error}
														helperText={error?.message}
														onChange={(e) =>
															onFieldChange("SampleSequence", e.target.value)
														}
														onFocus={onEditingStarted}
														onBlur={onEditingStopped}
														disabled={isDisabled}
														slotProps={{
															input: {
																endAdornment: isLoading ? (
																	<CircularProgress size={20} />
																) : undefined,
															},
														}}
													/>
												)}
											/>
										</Grid>
										<Grid size={{ xs: 12 }}>
											<Controller
												name="Technician"
												control={control}
												rules={{
													required: "Technician is required.",
													maxLength: 50,
												}}
												render={({ field, fieldState: { error } }) => (
													<TextField
														{...field}
														label="Technician"
														variant="outlined"
														size="small"
														fullWidth
														value={field.value ?? ""}
														error={!!error}
														helperText={error?.message}
														onChange={(e) =>
															onFieldChange("Technician", e.target.value)
														}
														onFocus={onEditingStarted}
														onBlur={onEditingStopped}
														disabled={isDisabled}
														slotProps={{
															input: {
																endAdornment: isLoading ? (
																	<CircularProgress size={20} />
																) : undefined,
															},
														}}
													/>
												)}
											/>
										</Grid>
									</Grid>
								</Grid>

								<Grid size={{ xs: 12, md: 8 }} style={{ marginTop: "0.4rem" }}>
									<Controller
										name="Comments"
										control={control}
										render={({ field }) => (
											<NotesFieldDE
												value={field.value ?? ""}
												onChange={(value) => onFieldChange("Comments", value)}
												onFocus={onEditingStarted}
												onBlur={onEditingStopped}
												disabled={isDisabled}
												height="100%"
												minRows={6}
												maxRows={6}
												resize="vertical"
											/>
										)}
									/>
								</Grid>

								<Grid size={{ xs: 12 }}>
									<Box
										sx={{
											display: "flex",
											justifyContent: "flex-end",
											gap: "10px",
											marginTop: "20px",
										}}
									>
										<Button
											variant="contained"
											color="inherit"
											onClick={onCancel}
											size="medium"
										>
											Cancel
										</Button>
										<ButtonWithLoading
											variant="contained"
											color="primary"
											type="submit"
											isLoading={isSaving}
											size="medium"
										>
											Save
										</ButtonWithLoading>
									</Box>
								</Grid>
							</Grid>
						</form>
					</Box>
				</Paper>
			</Grid>
		</Grid>
	);
};
